import {
  doc, getDoc, addDoc, updateDoc, collection, query, where, getDocs,
} from 'firebase/firestore';
import { db } from '../firebase';
import { getCollection } from './firestoreService';

const toDate = (v) => (!v ? null : (typeof v?.toDate === 'function' ? v.toDate() : new Date(v)));
const toYmd = (v) => { const d = toDate(v); return d ? d.toLocaleDateString('en-CA') : ''; };
const diffDays = (a, b) => {
  const ms = (toDate(b) || new Date()) - (toDate(a) || new Date());
  return Math.max(1, Math.ceil(ms / 86400000));
};

const normalize = (s) =>
  String(s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const parseCoded = (s) => {
  const t = String(s || '').trim();
  const idx = t.indexOf(' - ');
  if (idx > -1) return { code: t.slice(0, idx).trim(), name: t.slice(idx + 3).trim() };
  return { code: '', name: t };
};

const resolveDiagnosisText = (text, diagnoses = []) => {
  const parsed = parseCoded(text);
  if (parsed.code) return parsed;
  const n = normalize(text);
  const found = diagnoses.find((d) => normalize(d.diagnosis) === n || normalize(d.name) === n);
  if (found) return { code: found.code || found.id || '', name: found.diagnosis || found.name || text };
  return { code: '', name: text };
};

const parseDiagnosticosSecundarios = (text, diagnoses = []) => {
  if (!text) return [];
  return text
    .split(/[,;|]/)
    .map((s) => s.trim())
    .filter(Boolean)
    .map((s) => resolveDiagnosisText(s, diagnoses));
};

const normalizeTipoAlta = (condicion) => {
  const n = normalize(condicion);
  if (n.includes('fallecimiento') || n.includes('eutanasia')) return 'fallecido';
  if (n.includes('domicilio') || n.includes('condicional') || n.includes('cuidados paliativos')) return 'alta medica';
  if (n.includes('derivacion') || n.includes('otro centro')) return 'derivado';
  if (n.includes('voluntaria') || n.includes('propietario') || n.includes('solicitada') || n.includes('no regresa') || n.includes('abandono')) return 'no regresa';
  return n;
};

export const CONDICIONES_ALTA = [
  'Alta a Domicilio',
  'Derivación a otro Centro',
  'Fallecimiento',
  'Eutanasia',
  'Alta Voluntaria del Propietario',
];

export const CAUSAS_MUERTE = [
  'Falla multiorgánica',
  'Shock séptico',
  'Insuficiencia renal aguda',
  'Insuficiencia hepática',
  'Insuficiencia cardíaca',
  'Insuficiencia respiratoria',
  'Paro cardiorrespiratorio',
  'Enfermedad terminal / pronóstico grave',
  'Trauma / lesión no reversible',
  'Otra',
];

export const COMPLICACIONES_OPCIONES = [
  'Infecciones nosocomiales',
  'Reacciones adversas a medicamentos',
  'Dehiscencia de herida',
  'Complicaciones Anestésicas/Quirúrgicas',
  'Desequilibrios Metabólicos/Electrolíticos',
  'Neumonía Aspiración',
  'Sepsis',
  'Flebitis/Extravasación',
  'Otra',
];

/**
 * Crea o actualiza un egresoHospitalario a partir de una hospitalización dada de alta.
 * El upsert usa la llave `${ficha}_${fechaIngreso}`.
 */
export const syncEgresoHospitalario = async ({
  hospitalization,
  patient = null,
  epi = {},
  diagnoses = null,
  altaAt = new Date(),
}) => {
  if (!hospitalization?.admissionAt) return;

  const ficha = String(patient?.ficha || patient?.id || hospitalization.patientId || '').trim();
  if (!ficha) return;

  const fechaIngreso = toYmd(hospitalization.admissionAt);
  const fechaAlta = toYmd(altaAt);

  let pat = patient;
  if (!pat && hospitalization.patientId) {
    try {
      const pSnap = await getDoc(doc(db, 'patients', hospitalization.patientId));
      if (pSnap.exists()) pat = { id: pSnap.id, ...pSnap.data() };
    } catch (err) {
      console.error('Error cargando paciente para egreso:', err);
    }
  }

  let dxs = diagnoses;
  if (!dxs) {
    try {
      dxs = await getCollection('diagnoses');
    } catch (err) {
      console.error('Error cargando diagnósticos para egreso:', err);
      dxs = [];
    }
  }

  const procSnap = await getDocs(collection(db, 'hospitalizations', hospitalization.id, 'treatments'));
  const treatments = procSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
  const procedures = treatments.filter((t) => t.type === 'procedure');

  let reingreso = false;
  if (hospitalization.patientId) {
    try {
      const prevSnap = await getDocs(
        query(collection(db, 'hospitalizations'), where('patientId', '==', hospitalization.patientId))
      );
      reingreso = prevSnap.docs.some((d) => d.id !== hospitalization.id && d.data().status === 'alta');
    } catch (err) {
      console.error('Error consultando reingresos:', err);
    }
  }

  const diagnosticoIngreso = resolveDiagnosisText(hospitalization.primaryDiagnosis, dxs);
  const diagnosticoEgreso = resolveDiagnosisText(
    epi.diagnosticoDefinitivo || hospitalization.altaDiagnosticoEgreso,
    dxs
  );
  const diagnosticoPrincipal = diagnosticoEgreso;
  const diagnosticosSecundarios = parseDiagnosticosSecundarios(epi.diagnosticosSecundarios, dxs);

  const procPrincipal = procedures[0]
    ? { id: procedures[0].id, code: procedures[0].procedureCode || '', name: procedures[0].name || '' }
    : null;
  const procSecundarios = procedures.slice(1).map((p) => ({
    id: p.id, code: p.procedureCode || '', name: p.name || '',
  }));

  const condicionAlta = epi.condicionAlta || '';
  const causaMuerte = epi.causaMuerte || '';
  const tiposComplicaciones = epi.tiposComplicaciones || [];
  const complicaciones = !!epi.complicaciones || tiposComplicaciones.length > 0;
  const complicacionQx = complicaciones && tiposComplicaciones.includes('Complicaciones Anestésicas/Quirúrgicas');
  const complicacionPostQx = !!epi.complicacionPostQx;
  const fallecido = condicionAlta === 'Fallecimiento' || condicionAlta === 'Eutanasia';

  const egresoData = {
    ficha,
    fechaIngreso,
    fechaAlta,
    reingreso,
    complicaciones,
    complicacionQx,
    complicacionPostQx,
    tiposComplicaciones,
    descripcionComplicacion: epi.descripcionComplicacion || '',
    condicionAlta,
    causaMuerte: fallecido ? causaMuerte : '',
    diagnosticoIngreso,
    diagnosticoEgreso,
    diagnosticoPrincipal,
    diagnosticosSecundarios,
    tipoCaso: hospitalization.tipoIngreso || 'Hospitalización',
    casoQuirurgico: procedures.length > 0,
    procedimientoPrincipal: procPrincipal,
    procedimientosSecundarios: procSecundarios,
    tipoAltaNormalizado: normalizeTipoAlta(condicionAlta),
    fallecido,
    diasHospitalizacion: diffDays(hospitalization.admissionAt, altaAt),
    patientId: pat?.id || hospitalization.patientId || '',
    patientName: pat?.name || hospitalization.patientName || '',
    hospitalizationId: hospitalization.id,
    codificado: false,
    upsertKey: `${ficha}_${fechaIngreso}`,
    updatedAt: altaAt,
  };

  try {
    const q = query(
      collection(db, 'egresosHospitalarios'),
      where('ficha', '==', ficha),
      where('fechaIngreso', '==', fechaIngreso)
    );
    const snap = await getDocs(q);
    if (snap.empty) {
      await addDoc(collection(db, 'egresosHospitalarios'), { ...egresoData, createdAt: altaAt });
    } else {
      await updateDoc(doc(db, 'egresosHospitalarios', snap.docs[0].id), egresoData);
    }
  } catch (err) {
    console.error('Error haciendo upsert de egresoHospitalario:', err);
  }
};
